import {
  AudienceFilter,
  AudienceFilterOperator,
  AudienceFilterTimeOperator,
  AudienceFilterType,
} from '../../audience/components/audience.model';
import { DataPlatformAudienceRule } from '../data-platform.model';

/**
 * Inverse of `audienceToDpRules`: maps a single DataPlatformAudienceRule to
 * the legacy `AudienceFilter` shape so it can be persisted via AudienceService.
 *
 * Returns `null` for a rule the stored filter can't express, e.g. `funnel` or a "fewer than" goal count.
 */
export function dpRuleToAudienceFilter(rule: DataPlatformAudienceRule): AudienceFilter | null {
  switch (rule.type) {
    case 'goal_count':
      return goalCountToFilter(rule);

    case 'has_tag':
    case 'has_not_tag': {
      const tags = ruleValues(rule.values, rule.tag);
      return tags.length
        ? {
            type: AudienceFilterType.Tags,
            operator: listOperator(tags, rule.type === 'has_not_tag'),
            value: tags.join(','),
          }
        : null;
    }

    case 'purchase_count':
      return purchaseCountToFilter(rule);

    case 'is_identified':
      return { type: AudienceFilterType.Identified, operator: AudienceFilterOperator.Equals, value: 'true' };
    case 'is_anonymous':
      return { type: AudienceFilterType.Identified, operator: AudienceFilterOperator.Equals, value: 'false' };
    case 'is_logged_in':
      return { type: AudienceFilterType.LoggedIn, operator: AudienceFilterOperator.Equals, value: 'true' };
    case 'is_logged_out':
      return { type: AudienceFilterType.LoggedIn, operator: AudienceFilterOperator.Equals, value: 'false' };
    case 'has_consent':
    case 'has_not_consent': {
      const negated = rule.type === 'has_not_consent';
      const versions = ruleValues(rule.values, rule.versionDate);
      return versions.length
        ? { type: AudienceFilterType.Consent, operator: listOperator(versions, negated), value: versions.join(',') }
        : {
            type: AudienceFilterType.Consent,
            operator: AudienceFilterOperator.Exists,
            value: negated ? 'false' : 'true',
          };
    }

    case 'revenue':
      return revenueToFilter(rule, null);

    case 'interest_profile': {
      const ids = ruleValues(rule.values);
      const negated = rule.operator === 'not_any_of';
      if (!ids.length || (!negated && rule.operator !== 'any_of')) {
        return null;
      }
      return { type: AudienceFilterType.InterestProfiles, operator: listOperator(ids, negated), value: ids.join(',') };
    }

    case 'nps_score':
    case 'csat_score': {
      const operator = SCORE_OPERATORS[rule.operator ?? ''];
      if (!operator || rule.value == null || !Number.isInteger(rule.value)) {
        return null;
      }
      return {
        type: rule.type === 'nps_score' ? AudienceFilterType.NpsScore : AudienceFilterType.CsatScore,
        operator,
        value: String(rule.value),
      };
    }

    case 'last_active': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.LatestSession,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.LessThanOrEqual,
        value: (rule.daysAgo ?? 30).toString(),
      };
      if (rule.daysAgoUpper != null) {
        filter.upperValue = rule.daysAgoUpper;
        filter.operator = AudienceFilterOperator.Between;
      }
      return filter;
    }

    case 'event_count': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.EventCount,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 1).toString(),
        eventProperties: { eventName: rule.eventName },
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      encodeTimeframe(rule, filter);
      return filter;
    }

    case 'total_events': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.EventCount,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 1).toString(),
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      encodeTimeframe(rule, filter);
      return filter;
    }

    case 'widget_event_count': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.WidgetEventCount,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 1).toString(),
        widgetEventProperties: {
          widgetId: rule.widgetId,
          widgetEventType: rule.widgetEventType,
        },
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      encodeTimeframe(rule, filter);
      return filter;
    }

    case 'session_count': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.SessionCount,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 1).toString(),
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      encodeTimeframe(rule, filter);
      return filter;
    }

    case 'first_seen': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.FirstSeen,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.LessThanOrEqual,
        value: (rule.daysAgo ?? 30).toString(),
      };
      if (rule.daysAgoUpper != null) {
        filter.upperValue = rule.daysAgoUpper;
        filter.operator = AudienceFilterOperator.Between;
      }
      return filter;
    }

    case 'dimensions':
      return {
        type: AudienceFilterType.Dimension,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 1).toString(),
        dimensionProperties: { dimension: rule.dimension },
      };

    case 'dimension_affinity': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.DimensionAffinity,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 50).toString(),
        dimensionProperties: { dimension: rule.dimension },
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      return filter;
    }

    case 'engagement_score': {
      const filter: AudienceFilter = {
        type: AudienceFilterType.EngagementScore,
        operator: dpOpToFilterOp(rule.operator) ?? AudienceFilterOperator.GreaterThanOrEqual,
        value: (rule.value ?? 50).toString(),
      };
      if (rule.upperValue != null) filter.upperValue = rule.upperValue;
      return filter;
    }

    default:
      return null;
  }
}

export type RuleNumberProblem = 'missing' | 'score' | 'negative' | 'dayRange';

const DAYS_AGO_RULE_TYPES = new Set(['last_active', 'first_seen']);

const NUMBER_RULE_TYPES = new Set([
  'event_count',
  'goal_count',
  'revenue',
  'total_events',
  'session_count',
  'purchase_count',
  'engagement_score',
  'widget_event_count',
]);

export function ruleNumberProblem(rule: DataPlatformAudienceRule): RuleNumberProblem | null {
  if (!NUMBER_RULE_TYPES.has(rule.type) || rule.operator == null) {
    return null;
  }
  const bounds = rule.operator === 'between' ? [rule.value, rule.upperValue] : [rule.value];
  if (bounds.some((bound) => bound == null)) {
    return 'missing';
  }
  const numbers = bounds as number[];
  if (rule.type === 'engagement_score') {
    // DataPlatform scores profiles in whole numbers from 0 to 100.
    const ordered = numbers.length === 1 || numbers[0] <= numbers[1];
    return ordered && numbers.every((score) => Number.isInteger(score) && score >= 0 && score <= 100) ? null : 'score';
  }
  return numbers.some((bound) => bound < 0) ? 'negative' : null;
}

/** Edited rows only, so presets and suggestions keep their ranges; `upperValue` is stored as an int. */
export function dayRangeProblem(rule: DataPlatformAudienceRule): RuleNumberProblem | null {
  if (!DAYS_AGO_RULE_TYPES.has(rule.type) || rule.operator !== 'between') {
    return null;
  }
  if (rule.daysAgo == null || rule.daysAgoUpper == null) {
    return 'missing';
  }
  if (rule.daysAgo < 0) {
    return 'negative';
  }
  const wholeDays = Number.isInteger(rule.daysAgo) && Number.isInteger(rule.daysAgoUpper);
  return wholeDays && rule.daysAgoUpper > rule.daysAgo ? null : 'dayRange';
}

/** Drops rules the stored filter can't express. */
export function dpRulesToAudienceFilters(rules: readonly DataPlatformAudienceRule[]): AudienceFilter[] {
  return rulesToAudienceFilters(rules).filters;
}

/** A purchase count and revenue with the same timeframe become one Purchase filter, as the legacy builder stores them. */
export function rulesToAudienceFilters(rules: readonly DataPlatformAudienceRule[]): {
  filters: AudienceFilter[];
  unsavable: DataPlatformAudienceRule[];
} {
  const filters: AudienceFilter[] = [];
  const unsavable: DataPlatformAudienceRule[] = [];
  const pairedCounts = pairPurchaseCountsWithRevenue(rules);
  const mergedCounts = new Set(pairedCounts.values());
  for (const rule of rules) {
    if (mergedCounts.has(rule)) {
      continue;
    }
    const filter = ruleNumberProblem(rule)
      ? null
      : rule.type === 'revenue'
        ? revenueToFilter(rule, pairedCounts.get(rule) ?? null)
        : dpRuleToAudienceFilter(rule);
    if (filter) {
      filters.push(filter);
    } else {
      unsavable.push(rule);
    }
  }
  return { filters, unsavable };
}

function pairPurchaseCountsWithRevenue(
  rules: readonly DataPlatformAudienceRule[],
): Map<DataPlatformAudienceRule, DataPlatformAudienceRule> {
  const pairs = new Map<DataPlatformAudienceRule, DataPlatformAudienceRule>();
  const claimed = new Set<DataPlatformAudienceRule>();
  for (const revenue of rules.filter((rule) => rule.type === 'revenue')) {
    const count = rules.find(
      (rule) =>
        rule.type === 'purchase_count' &&
        !claimed.has(rule) &&
        !rule.couponOnly &&
        !countMeansNone(rule) &&
        countToFrequency(rule) !== null &&
        sameTimeframe(rule, revenue),
    );
    if (count) {
      claimed.add(count);
      pairs.set(revenue, count);
    }
  }
  return pairs;
}

function goalCountToFilter(rule: DataPlatformAudienceRule): AudienceFilter | null {
  const goals = ruleValues(rule.values, rule.eventName);
  if (!goals.length) {
    return null;
  }
  if (countMeansNone(rule)) {
    const filter: AudienceFilter = {
      type: AudienceFilterType.Goals,
      operator: listOperator(goals, true),
      value: goals.join(','),
    };
    encodeTimeframe(rule, filter);
    return filter;
  }
  const frequency = countToFrequency(rule);
  if (!frequency) {
    return null;
  }
  const filter: AudienceFilter = {
    type: AudienceFilterType.Goals,
    operator: listOperator(goals, false),
    value: goals.join(','),
    ...frequency,
  };
  encodeTimeframe(rule, filter);
  return filter;
}

function purchaseCountToFilter(rule: DataPlatformAudienceRule): AudienceFilter | null {
  // The check reads a coupon as "used one at least once", not as a count.
  if (rule.couponOnly) {
    return null;
  }
  if (countMeansNone(rule)) {
    const filter: AudienceFilter = { type: AudienceFilterType.Purchase, operator: AudienceFilterOperator.NotEquals };
    encodeTimeframe(rule, filter);
    return filter;
  }
  const frequency = countToFrequency(rule);
  if (!frequency) {
    return null;
  }
  const filter: AudienceFilter = {
    type: AudienceFilterType.Purchase,
    operator: AudienceFilterOperator.Equals,
    ...frequency,
  };
  encodeTimeframe(rule, filter);
  return filter;
}

function revenueToFilter(
  rule: DataPlatformAudienceRule,
  count: DataPlatformAudienceRule | null,
): AudienceFilter | null {
  const properties = revenueProperties(rule);
  if (!properties) {
    return null;
  }
  const filter: AudienceFilter = {
    type: AudienceFilterType.Purchase,
    operator: AudienceFilterOperator.Equals,
    ...(count ? countToFrequency(count) : {}),
    purchaseProperties: { ...properties, appliedCoupon: rule.couponOnly ?? false },
  };
  encodeTimeframe(rule, filter);
  return filter;
}

/** The check truncates revenue to whole numbers, so ≥ X is stored as > X-1 and ≤ X as < X+1. */
function revenueProperties(
  rule: DataPlatformAudienceRule,
): { operator: string; value: number; upperValue: number } | null {
  const value = rule.value;
  if (value == null) {
    return null;
  }
  switch (rule.operator) {
    case 'gt':
      return { operator: 'MoreThan', value: Math.floor(value), upperValue: 0 };
    case 'gte':
      return { operator: 'MoreThan', value: Math.ceil(value) - 1, upperValue: 0 };
    case 'lt':
      return { operator: 'LessThan', value: Math.ceil(value), upperValue: 0 };
    case 'lte':
      return { operator: 'LessThan', value: Math.floor(value) + 1, upperValue: 0 };
    case 'eq':
      return Number.isInteger(value) ? { operator: 'Exactly', value, upperValue: 0 } : null;
    case 'between':
      return rule.upperValue == null
        ? null
        : { operator: 'Between', value: Math.ceil(value), upperValue: Math.floor(rule.upperValue) };
    default:
      return null;
  }
}

function countMeansNone(rule: DataPlatformAudienceRule): boolean {
  const value = rule.value;
  if (value == null) {
    return false;
  }
  return (
    (rule.operator === 'eq' && value === 0) ||
    (rule.operator === 'lt' && value > 0 && value <= 1) ||
    (rule.operator === 'lte' && value >= 0 && value < 1)
  );
}

/** The stored frequency is "at least" or "exactly" N, with N of one or more. */
function countToFrequency(rule: DataPlatformAudienceRule): { frequencyType: string; frequencyValue: number } | null {
  if (rule.operator != null && rule.value == null) {
    return null;
  }
  const value = rule.value ?? 1;
  switch (rule.operator ?? 'gte') {
    case 'gte':
      return value > 0 ? { frequencyType: 'AtLeast', frequencyValue: Math.ceil(value) } : null;
    case 'gt':
      return value >= 0 ? { frequencyType: 'AtLeast', frequencyValue: Math.floor(value) + 1 } : null;
    case 'eq':
      return Number.isInteger(value) && value >= 1 ? { frequencyType: 'Exactly', frequencyValue: value } : null;
    default:
      return null;
  }
}

function sameTimeframe(a: DataPlatformAudienceRule, b: DataPlatformAudienceRule): boolean {
  return (
    a.daysAgo === b.daysAgo &&
    a.daysAgoUpper === b.daysAgoUpper &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate
  );
}

/** Untrimmed, as the check compares a single value. */
function ruleValues(values: readonly string[] | undefined, single?: string): string[] {
  const all = values?.length ? values : single ? [single] : [];
  return [...new Set(all.filter((value) => value.trim().length > 0))];
}

function listOperator(values: readonly string[], negated: boolean): AudienceFilterOperator {
  if (values.length > 1) {
    return negated ? AudienceFilterOperator.NotAnyOf : AudienceFilterOperator.AnyOf;
  }
  return negated ? AudienceFilterOperator.NotEquals : AudienceFilterOperator.Equals;
}

const SCORE_OPERATORS: Readonly<Partial<Record<string, AudienceFilterOperator>>> = {
  gt: AudienceFilterOperator.GreaterThan,
  lt: AudienceFilterOperator.LessThan,
};

function dpOpToFilterOp(op?: string): AudienceFilterOperator | null {
  switch (op) {
    case 'eq':
      return AudienceFilterOperator.Equals;
    case 'gt':
      return AudienceFilterOperator.GreaterThan;
    case 'gte':
      return AudienceFilterOperator.GreaterThanOrEqual;
    case 'lt':
      return AudienceFilterOperator.LessThan;
    case 'lte':
      return AudienceFilterOperator.LessThanOrEqual;
    case 'between':
      return AudienceFilterOperator.Between;
    default:
      return null;
  }
}

function encodeTimeframe(rule: DataPlatformAudienceRule, filter: AudienceFilter): void {
  if (rule.daysAgoUpper != null && rule.daysAgo != null) {
    filter.timeOperator = AudienceFilterTimeOperator.BetweenDaysAgo;
    filter.timeValue = JSON.stringify({ days: rule.daysAgo, upperDays: rule.daysAgoUpper });
  } else if (rule.startDate && rule.endDate) {
    filter.timeOperator = AudienceFilterTimeOperator.BetweenDates;
    filter.timeValue = JSON.stringify({ startDate: rule.startDate, endDate: rule.endDate });
  } else if (rule.startDate) {
    filter.timeOperator = AudienceFilterTimeOperator.SinceDate;
    filter.timeValue = JSON.stringify({ startDate: rule.startDate });
  } else if (rule.daysAgo != null) {
    filter.timeOperator = AudienceFilterTimeOperator.TimeWindow;
    filter.timeValue = JSON.stringify({ days: rule.daysAgo });
  }
}
